import React, { useState, useEffect } from 'react';
import PlayerService from '../../services/PlayerService';
import DiscordAuthService from '../../services/DiscordAuthService';
import audioManager from '../../utils/filemanager/AudioManager';
import accountStore from './AccountStore';
import { setCookie, getCookie, removeCookie } from '../../utils/CookieManager';
import { clearDiscordUID } from '../../utils/CrossDomainAuth';
import Leaderboard from './Leaderboard';
import Statistics from './Statistics';
import ProfileSidebar from './ProfileSidebar';

const LinkAccount = ({ onAccountLinked }) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [error, setError] = useState('');
  const [linkedProfile, setLinkedProfile] = useState(accountStore.getProfile());

  useEffect(() => {
    let isSubscribed = true;

    const loadProfile = async () => {
      if (!isSubscribed) return;

      try {
        const profile = accountStore.getProfile();
        let validProfile = null;

        // First try to validate current profile
        if (profile?.playerId) {
          const freshData = await PlayerService.getPlayerData(profile.playerId);
          if (freshData?.data) {
            validProfile = freshData;
          }
        }

        // If no valid profile, try to recover from stored data
        if (!validProfile) {
          let storedProfile = null;
          let discordUID = null;

          // Try to get stored profile
          storedProfile = getCookie('linkedProfile');

          // Try to get Discord UID
          discordUID = getCookie('discordUID');
          
          // Store Discord UID in localStorage for cross-domain access
          if (discordUID) {
            localStorage.setItem('discord_uid', discordUID);
          }

          // Try to validate stored profile
          if (storedProfile?.playerId) {
            const freshData = await PlayerService.getPlayerData(storedProfile.playerId);
            if (freshData?.data) {
              validProfile = freshData;
            }
          }

          // If still no valid profile, try Discord UID
          if (!validProfile && discordUID) {
            const playerData = await PlayerService.getPlayerData(discordUID);
            if (playerData?.data) {
              validProfile = playerData;
              // Store the valid profile
              setCookie('linkedProfile', playerData);
            }
          }
        }

        if (!isSubscribed) return;

        if (validProfile) {
          accountStore.setProfile(validProfile);
          setLinkedProfile(validProfile);
          onAccountLinked(validProfile);
        } else {
          accountStore.setProfile(null);
          setLinkedProfile(null);
          // Clean up storage
          removeCookie('linkedProfile');
          removeCookie('discordUID');
        }
      } catch (error) {
        if (!isSubscribed) return;
        console.error('Error during automatic login:', error);
        accountStore.setProfile(null);
        setLinkedProfile(null);
        // Clean up storage
        removeCookie('linkedProfile');
        removeCookie('discordUID');
      }
    };

    loadProfile();

    const unsubscribe = accountStore.subscribe((profile) => {
      if (isSubscribed) {
        setLinkedProfile(profile);
      }
    });

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, []);

  const handleLogout = () => {
    console.log('=== LinkAccount LOGOUT DEBUG ===');
    console.log('Before logout - localStorage discord_uid:', localStorage.getItem('discord_uid'));
    
    const result = accountStore.logout();
    
    // Clear cross-domain authentication for axis-zero game
    console.log('Calling clearDiscordUID...');
    clearDiscordUID();
    console.log('After clearDiscordUID - localStorage discord_uid:', localStorage.getItem('discord_uid'));
    
    onAccountLinked(result);
    setLinkedProfile(result);
    setShowSidebar(false);
    audioManager.playFail();
    
    console.log('Final check before reload - localStorage discord_uid:', localStorage.getItem('discord_uid'));
    
    // Add a small delay to ensure the fail sound plays before refresh
    setTimeout(() => {
      console.log('About to reload page...');
      window.location.reload();
    }, 500);
  };

  return (
    <>
      {/* Profile Icon Button */}
      <div
        onClick={() => setShowSidebar(prev => !prev)}
        role="button"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          cursor: 'pointer',
          width: '44px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid #33ff33',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.5)',
          boxShadow: '0 0 20px rgba(51, 255, 51, 0.2)',
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          animation: 'crtFlicker 0.15s infinite',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)'
        }}
        onMouseEnter={e => {
          e.target.style.background = 'rgba(51, 255, 51, 0.1)';
          e.target.style.boxShadow = '0 0 20px rgba(51, 255, 51, 0.4)';
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={e => {
          e.target.style.background = 'rgba(0, 0, 0, 0.5)';
          e.target.style.boxShadow = '0 0 20px rgba(51, 255, 51, 0.2)';
          e.target.style.transform = 'scale(1)';
        }}
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="#33ff33" 
          strokeWidth="2"
          style={{
            filter: 'drop-shadow(0 0 8px #33ff33)',
          }}
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </div>

      {/* Sidebar */}
      {showSidebar && (
        <ProfileSidebar
          onClose={() => setShowSidebar(false)}
          onShowLeaderboard={() => {
            setShowLeaderboard(true);
            setShowSidebar(false);
          }}
          onShowStatistics={() => {
            setShowStatistics(true);
            setShowSidebar(false);
          }}
          onLogout={handleLogout}
          profile={linkedProfile}
        />
      )}

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <Leaderboard onClose={() => setShowLeaderboard(false)} />
      )}

      {/* Statistics Modal */}
      {showStatistics && (
        <Statistics onClose={() => setShowStatistics(false)} />
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '70px',
          right: '20px',
          zIndex: 10000,
          color: '#ff3333',
          fontSize: '14px',
          textShadow: '0 0 8px #ff3333',
          animation: 'glitch 0.3s infinite',
          padding: '12px',
          background: 'rgba(255, 51, 51, 0.1)',
          border: '1px solid rgba(255, 51, 51, 0.3)',
          borderRadius: '4px',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)'
        }}>
          {error}
        </div>
      )}
    </>
  );
};

export default LinkAccount;